import { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatDate, cn } from '@/lib/utils'
import { MetricCard, LoadingSpinner } from '@/components/ui'
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts'

/** Collapsible section wrapper with lazy loading support */
function CollapsibleSection({ title, defaultOpen = false, children, onToggle }: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
  onToggle?: (open: boolean) => void
}) {
  const [open, setOpen] = useState(defaultOpen)
  const toggle = useCallback(() => {
    const next = !open
    setOpen(next)
    onToggle?.(next)
  }, [open, onToggle])
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full p-4 border-b border-border flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
      >
        <h2 className="font-semibold">{title}</h2>
        <span className="text-muted-foreground text-sm">{open ? '▲ Collapse' : '▼ Expand'}</span>
      </button>
      {open && children}
    </div>
  )
}

const STATUS_COLORS: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  completed: 'bg-green-100 text-green-800',
  processed: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
}

const FUND_COLORS = [
  'hsl(217, 91%, 30%)', 'hsl(142, 76%, 36%)', 'hsl(0, 84%, 60%)',
  'hsl(38, 92%, 50%)', 'hsl(262, 83%, 58%)', 'hsl(199, 89%, 48%)',
  'hsl(24, 95%, 53%)', 'hsl(330, 81%, 60%)', 'hsl(180, 60%, 40%)',
  'hsl(290, 60%, 50%)', 'hsl(50, 80%, 45%)', 'hsl(160, 70%, 35%)',
]

const STACKED_COLORS: Record<string, string> = {
  'Equity (NNL)': 'hsl(217, 91%, 40%)', 'Equity (NNS)': 'hsl(217, 91%, 65%)',
  'Yields (NNL)': 'hsl(142, 76%, 36%)', 'Yields (NNS)': 'hsl(142, 76%, 60%)',
  'Credit (NNL)': 'hsl(38, 92%, 45%)', 'Credit (NNS)': 'hsl(38, 92%, 70%)',
  'Convertibles (NNL)': 'hsl(262, 83%, 45%)', 'Convertibles (NNS)': 'hsl(262, 83%, 70%)',
  'Currency (NNL)': 'hsl(199, 89%, 40%)', 'Currency (NNS)': 'hsl(199, 89%, 65%)',
  'Commodities (NNL)': 'hsl(24, 95%, 45%)', 'Commodities (NNS)': 'hsl(24, 95%, 70%)',
  'Privates (NNL)': 'hsl(330, 81%, 45%)', 'Privates (NNS)': 'hsl(330, 81%, 70%)',
}

const ASSET_CLASSES = [
  { code: '2', name: 'Equity' },
  { code: '3', name: 'Yields' },
  { code: '4', name: 'Credit' },
  { code: '5', name: 'Convertibles' },
  { code: '6', name: 'Currency' },
  { code: '7', name: 'Commodities' },
]

function shortDate(d: string) {
  if (!d) return ''
  return d.substring(0, 7) // YYYY-MM
}

/** Multi-fund line chart reused for gross exposure, investor conc, VaR, etc. */
function MultiFundLineChart({ series, height = 280, yLabel, isPct }: {
  series: { fund_name: string; data: { date: string; value: number }[] }[]
  height?: number
  yLabel?: string
  isPct?: boolean
}) {
  // Merge all fund data into recharts-compatible array keyed by date
  const merged = useMemo(() => {
    const dateMap: Record<string, Record<string, any>> = {}
    series?.forEach((s) => {
      s.data?.forEach((d) => {
        if (!dateMap[d.date]) dateMap[d.date] = { date: d.date }
        dateMap[d.date][s.fund_name] = d.value
      })
    })
    return Object.values(dateMap).sort((a, b) => String(a.date).localeCompare(String(b.date)))
  }, [series])

  if (!series?.length) return <div className="text-muted-foreground text-sm p-4">No data available</div>

  const fundNames = series.map((s) => s.fund_name)

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={merged} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(214,32%,91%)" />
        <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={shortDate} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={isPct ? (v: number) => `${v}%` : undefined} label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fontSize: 11 } : undefined} />
        <Tooltip contentStyle={{ fontSize: 12, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }} formatter={(v: any) => isPct ? `${Number(v).toFixed(2)}%` : Number(v).toFixed(4)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {fundNames.map((fn, i) => (
          <Line key={fn} type="monotone" dataKey={fn} stroke={FUND_COLORS[i % FUND_COLORS.length]} strokeWidth={2} dot={false} activeDot={{ r: 3 }} connectNulls />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

export default function OperaMonitoring() {
  const [selectedFund, setSelectedFund] = useState<string | null>(null)
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [stressMonthsAgo, setStressMonthsAgo] = useState(1)
  const [stressExpanded, setStressExpanded] = useState(false)
  const [fileTrackingExpanded, setFileTrackingExpanded] = useState(false)

  // --- Queries ---
  const { data: funds } = useQuery({
    queryKey: ['opera-funds'],
    queryFn: () => api.get('/opera/funds').then((r) => r.data),
  })

  const { data: dates, isLoading: datesLoading } = useQuery({
    queryKey: ['opera-dates'],
    queryFn: () => api.get('/opera/dates').then((r) => r.data),
  })

  const { data: recentReports, isLoading: reportsLoading } = useQuery({
    queryKey: ['opera-recent'],
    queryFn: () => api.get('/opera/reports/recent').then((r) => r.data),
  })

  const { data: grossExposure } = useQuery({
    queryKey: ['opera-gross-exposure'],
    queryFn: () => api.get('/opera/gross-exposure').then((r) => r.data),
  })

  // AIFMD metric codes carry spaces and, for 1.7.1, a literal '%'. Interpolated raw
  // that becomes ".../1.7.1%20(%)" — an invalid percent-escape the server rejects with
  // a 400, so the investor-concentration chart never loaded. Encode the code itself.
  const { data: investorConc } = useQuery({
    queryKey: ['opera-investor-conc'],
    queryFn: () => api.get(`/opera/metric-series/${encodeURIComponent('1.7.1 (%)')}`).then((r) => r.data),
  })

  const { data: varData } = useQuery({
    queryKey: ['opera-var'],
    queryFn: () => api.get(`/opera/metric-series/${encodeURIComponent('8.5.8 (VaR)')}`).then((r) => r.data),
  })

  // Asset class exposures (6 classes)
  const assetExposureQueries = ASSET_CLASSES.map((ac) =>
    useQuery({
      queryKey: ['opera-asset-exposure', ac.code],
      queryFn: () => api.get(`/opera/asset-exposure/${ac.code}`).then((r) => r.data),
    })
  )

  const { data: privatesExp } = useQuery({
    queryKey: ['opera-privates-exposure'],
    queryFn: () => api.get('/opera/privates-exposure').then((r) => r.data),
  })

  const { data: stressTest, isLoading: stressLoading } = useQuery({
    queryKey: ['opera-stress-test', stressMonthsAgo],
    queryFn: () => api.get(`/opera/stress-test?months_ago=${stressMonthsAgo}`).then((r) => r.data),
    enabled: stressExpanded,
  })

  const { data: fileTracking, isLoading: fileTrackingLoading } = useQuery({
    queryKey: ['opera-file-tracking'],
    queryFn: () => api.get('/opera/file-tracking').then((r) => r.data),
    enabled: fileTrackingExpanded,
  })

  // Fund-specific queries
  const { data: stackedExposure } = useQuery({
    queryKey: ['opera-stacked', selectedFund],
    queryFn: () => api.get(`/opera/fund/${encodeURIComponent(selectedFund!)}/stacked-exposure`).then((r) => r.data),
    enabled: !!selectedFund,
  })

  const { data: fundSections } = useQuery({
    queryKey: ['opera-sections', selectedFund],
    queryFn: () => api.get(`/opera/fund/${encodeURIComponent(selectedFund!)}/sections`).then((r) => r.data),
    enabled: !!selectedFund,
  })

  const { data: sectionMetrics } = useQuery({
    queryKey: ['opera-section-metrics', selectedFund, selectedSection],
    queryFn: () => api.get(`/opera/fund/${encodeURIComponent(selectedFund!)}/section-metrics?section=${selectedSection}`).then((r) => r.data),
    enabled: !!selectedFund && !!selectedSection,
  })

  // --- KPI computations ---
  const kpis = useMemo(() => {
    if (!recentReports?.length) return { totalReports: 0, totalFunds: 0, latestUpload: '-' }
    const totalFunds = recentReports.length
    const totalReports = recentReports.reduce((s: number, _: any) => s + 1, 0)
    const uploads = recentReports.map((r: any) => r.upload_date).filter(Boolean).sort()
    return { totalReports, totalFunds, latestUpload: uploads.length ? formatDate(uploads[uploads.length - 1]) : '-' }
  }, [recentReports])

  // --- Stacked bar chart data ---
  const stackedChartData = useMemo(() => {
    if (!stackedExposure?.dates?.length) return []
    return stackedExposure.dates.map((d: string, i: number) => {
      const row: Record<string, any> = { date: d }
      stackedExposure.series?.forEach((s: any) => {
        row[s.label] = s.values[i] ?? 0
      })
      return row
    })
  }, [stackedExposure])

  if (datesLoading && reportsLoading) {
    return <div className="flex justify-center p-8"><LoadingSpinner /></div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">OPERA Monitoring</h1>
        <p className="text-muted-foreground">AIFMD regulatory reporting - OPERA Reports Dashboard</p>
      </div>

      {/* --- KPI Cards --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Total Funds" value={kpis.totalFunds} />
        <MetricCard label="Total Reports" value={kpis.totalReports} />
        <MetricCard label="Latest Upload" value={kpis.latestUpload} />
      </div>

      {/* --- Section 1: Most Updated Date by Fund --- */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border"><h2 className="font-semibold">Most Updated Date by Fund</h2></div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr><th>Fund</th><th>Latest Report Date</th><th className="text-right">Report Count</th></tr>
            </thead>
            <tbody>
              {dates?.map((d: any) => (
                <tr key={d.fund_name}>
                  <td className="font-medium">{d.fund_name}</td>
                  <td>{formatDate(d.latest_date)}</td>
                  <td className="text-right font-mono">{d.report_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Section 2: Recent Reports Table --- */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border"><h2 className="font-semibold">Recent OPERA Reports</h2></div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fund</th><th>Report Date</th><th>Upload Date</th>
                <th className="text-right">MTD</th><th className="text-right">YTD</th>
                <th className="text-right">Days Old</th>
              </tr>
            </thead>
            <tbody>
              {recentReports?.map((r: any) => (
                <tr key={r.fund_name}>
                  <td className="font-medium">{r.fund_name}</td>
                  <td>{formatDate(r.report_date)}</td>
                  <td>{formatDate(r.upload_date)}</td>
                  <td className={cn('text-right font-mono', r.mtd_return >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {formatPercent(r.mtd_return)}
                  </td>
                  <td className={cn('text-right font-mono', r.ytd_return >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {formatPercent(r.ytd_return)}
                  </td>
                  <td className={cn('text-right', r.days_since_report > 30 ? 'text-red-600 font-bold' : '')}>{r.days_since_report}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Section 3: Gross Exposure Across All Asset Classes --- */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="font-semibold mb-3">Gross Exposure Across All Asset Classes (% of AUM)</h2>
        <MultiFundLineChart series={grossExposure || []} yLabel="Gross Exposure %" isPct height={320} />
      </div>

      {/* --- Section 4: Investor Concentration + VaR --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Investor Concentration - Top 5 Largest (1.7.1)</h2>
          <MultiFundLineChart
            series={investorConc?.series || []}
            isPct={investorConc?.is_percentage}
            yLabel={investorConc?.is_percentage ? '%' : undefined}
          />
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Value at Risk (8.5.8)</h2>
          <MultiFundLineChart
            series={varData?.series || []}
            isPct={varData?.is_percentage}
            yLabel={varData?.is_percentage ? '%' : undefined}
          />
        </div>
      </div>

      {/* --- Section 5: Per-Asset-Class Exposure (6 asset classes x 2 charts) --- */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="font-semibold mb-4">Asset Class Exposure Analysis</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {ASSET_CLASSES.map((ac, idx) => {
            const q = assetExposureQueries[idx]
            const d = q.data
            return (
              <div key={ac.code} className="space-y-4">
                {/* Gross */}
                <div className="border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2">{ac.name} - Gross Exposure</h3>
                  <MultiFundLineChart series={d?.gross || []} height={220} />
                </div>
                {/* Net */}
                <div className="border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2">{ac.name} - Net Exposure</h3>
                  <MultiFundLineChart series={d?.net || []} height={220} />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* --- Section 6: Privates Aggregate Exposure --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Privates - Gross Exposure (% of AUM)</h2>
          <MultiFundLineChart series={privatesExp?.gross || []} isPct height={280} yLabel="Gross %" />
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Privates - Net Exposure (% of AUM)</h2>
          <MultiFundLineChart series={privatesExp?.net || []} isPct height={280} yLabel="Net %" />
        </div>
      </div>

      {/* --- Section 7: Stress Test Table (Collapsible, Lazy) --- */}
      <CollapsibleSection title="OPERA Stress Test" onToggle={setStressExpanded}>
        <div className="p-4 border-b border-border flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <label>Months ago:</label>
            <input
              type="range" min={1} max={stressTest?.max_observations || 24} value={stressMonthsAgo}
              onChange={(e) => setStressMonthsAgo(Number(e.target.value))}
              className="w-40"
            />
            <span className="font-mono w-6 text-center">{stressMonthsAgo}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          {stressLoading ? (
            <div className="flex justify-center p-6"><LoadingSpinner /></div>
          ) : stressTest?.funds?.length ? (
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10">Metric</th>
                  {stressTest.funds.map((fn: string) => (
                    <th key={fn} className="text-right whitespace-nowrap">{fn}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Date row */}
                <tr className="bg-muted/50">
                  <td className="sticky left-0 bg-muted/50 z-10 font-medium">Report Date</td>
                  {stressTest.funds.map((fn: string) => (
                    <td key={fn} className="text-right font-mono">{stressTest.dates?.[fn] ? shortDate(stressTest.dates[fn]) : '-'}</td>
                  ))}
                </tr>
                {stressTest.metrics?.map((m: any) => (
                  <tr key={m.id}>
                    <td className="sticky left-0 bg-card z-10 font-medium whitespace-nowrap" title={m.label}>{m.id}</td>
                    {stressTest.funds.map((fn: string) => {
                      const v = stressTest.data?.[m.id]?.[fn]
                      return (
                        <td key={fn} className={cn('text-right font-mono', v != null && v < 0 ? 'text-red-600' : v != null && v > 0 ? 'text-green-600' : '')}>
                          {v != null ? `${v.toFixed(2)}%` : '-'}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-4 text-muted-foreground text-sm">No stress test data available</div>
          )}
        </div>
      </CollapsibleSection>

      {/* --- Section 7b: File Tracking (Collapsible, Lazy) --- */}
      <CollapsibleSection title="OPERA File Tracking" onToggle={setFileTrackingExpanded}>
        <div className="overflow-x-auto">
          {fileTrackingLoading ? (
            <div className="flex justify-center p-6"><LoadingSpinner /></div>
          ) : fileTracking?.length ? (
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Fund Name</th>
                  <th>Modified Date</th>
                  <th>Processed Date</th>
                  <th>Status</th>
                  <th className="text-right">Records</th>
                  <th>Error Message</th>
                </tr>
              </thead>
              <tbody>
                {fileTracking.map((f: any) => {
                  const statusKey = (f.processing_status || '').toLowerCase()
                  const badgeClass = STATUS_COLORS[statusKey] || 'bg-gray-100 text-gray-800'
                  return (
                    <tr key={f.id}>
                      <td className="font-medium whitespace-nowrap max-w-[200px] truncate" title={f.file_name}>{f.file_name}</td>
                      <td className="whitespace-nowrap">{f.fund_name}</td>
                      <td className="whitespace-nowrap">{formatDate(f.file_modified_date)}</td>
                      <td className="whitespace-nowrap">{formatDate(f.processed_date)}</td>
                      <td>
                        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', badgeClass)}>
                          {f.processing_status || '-'}
                        </span>
                      </td>
                      <td className="text-right font-mono">{f.records_processed ?? '-'}</td>
                      <td className="text-red-600 max-w-[300px] truncate" title={f.error_message || ''}>{f.error_message || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-4 text-muted-foreground text-sm">No file tracking data available</div>
          )}
        </div>
      </CollapsibleSection>

      {/* --- Section 8: Fund Selection --- */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="font-semibold mb-3">Select Fund for Detailed Analysis</h2>
        <div className="flex flex-wrap gap-2">
          {funds?.map((f: any) => (
            <button
              key={f.fund_name}
              onClick={() => { setSelectedFund(f.fund_name); setSelectedSection(null) }}
              className={cn(
                'px-3 py-1.5 rounded-md text-sm border transition-colors',
                selectedFund === f.fund_name
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border hover:bg-muted'
              )}
            >
              {f.fund_name}
            </button>
          ))}
        </div>
      </div>

      {/* --- Section 9+: Fund-specific content --- */}
      {selectedFund && (
        <>
          {/* Stacked bar chart */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="font-semibold mb-3">Gross Exposure by Asset Class - {selectedFund}</h2>
            {stackedChartData.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={stackedChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214,32%,91%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={shortDate} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: 11, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }} formatter={(v: number) => `${v.toFixed(2)}%`} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {stackedExposure?.series?.map((s: any) => (
                    <Bar key={s.label} dataKey={s.label} stackId="a" fill={STACKED_COLORS[s.label] || '#888'} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-muted-foreground text-sm">No stacked exposure data</div>
            )}
          </div>

          {/* Section filter */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="font-semibold mb-3">OPERA Sections - {selectedFund}</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              {fundSections?.map((s: any) => (
                <button
                  key={s.section}
                  onClick={() => setSelectedSection(selectedSection === s.section ? null : s.section)}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-sm border transition-colors',
                    selectedSection === s.section
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border hover:bg-muted'
                  )}
                >
                  Section {s.section} ({s.metric_count})
                </button>
              ))}
            </div>

            {/* Section metrics charts */}
            {selectedSection && sectionMetrics?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sectionMetrics.map((m: any) => (
                  <div key={m.id} className="border border-border rounded-lg p-3">
                    <h4 className="text-xs font-semibold mb-2 truncate" title={m.label}>{m.label}</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={m.data} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(214,32%,91%)" />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={shortDate} />
                        <YAxis tick={{ fontSize: 9 }} tickFormatter={m.is_percentage ? (v: number) => `${v}%` : undefined} />
                        <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v: number) => m.is_percentage ? `${v.toFixed(2)}%` : v.toFixed(4)} />
                        <Line type="monotone" dataKey="value" stroke="hsl(217,91%,30%)" strokeWidth={2} dot={false} />
                        {/* Upper bound */}
                        {m.data.some((d: any) => d.upper_bound != null) && (
                          <Line type="monotone" dataKey="upper_bound" stroke="hsl(0,84%,60%)" strokeWidth={1} strokeDasharray="5 5" dot={false} name="Upper Bound" />
                        )}
                        {/* Lower bound */}
                        {m.data.some((d: any) => d.lower_bound != null) && (
                          <Line type="monotone" dataKey="lower_bound" stroke="hsl(142,76%,36%)" strokeWidth={1} strokeDasharray="5 5" dot={false} name="Lower Bound" />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            ) : selectedSection ? (
              <div className="text-muted-foreground text-sm p-4">No metrics data for this section</div>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}
